import QueryString from 'qs';
import { api } from './configs/axiosConfig';
import { NOTIFICATION_TITLES } from '@/utils/notificationUtils';
import { arrayBufferToBase64 } from '@/utils/utilities';
import { buildFeedbackEmailHtml, buildFeedbackOwnerEmailHtml } from '@/utils/feedbackEmailTemplate';
export const NotificationAPI = {
    async getNotificationBundle(userId, offset = 0, limit = 100, channels = ['pull'], platformHost = '') {
        const to = userId;
        // Calculate date from 6 months ago with ge (greater than or equal) filter
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        const created = `ge${sixMonthsAgo.toISOString()}`;
        const queryParams = {
            limit,
            offset,
            to,
            channels,
            created
        };
        const response = await api.get('/Notifications/', {
            baseURL: platformHost,
            params: queryParams,
            paramsSerializer: (params) => QueryString.stringify(params, {
                arrayFormat: 'repeat',
                encode: false
            })
        });
        return response.data;
    },
    async markNotificationAsRead(messagesIdList, expires, platformHost = '') {
        const requestBody = {
            notification: messagesIdList.map((id) => {
                const notification = {
                    id,
                    viewedDateTime: new Date().toISOString()
                };
                if (expires !== undefined) {
                    notification.expires = expires;
                }
                return notification;
            })
        };
        const response = await api.patch('/Notifications/', requestBody, {
            baseURL: platformHost,
            params: { mark_all_as_read: true }
        });
        return response.data;
    },
    async syncSubscription(subscription, subscriptionStatus, userId, platformHost = '') {
        const keys = subscription.getKey
            ? {
                p256dh: subscription.getKey('p256dh'),
                auth: subscription.getKey('auth')
            }
            : null;
        const subscriptionData = {
            endpoint_details: [
                {
                    endpoint: subscription.endpoint,
                    keys: keys
                        ? {
                            p256dh: arrayBufferToBase64(keys.p256dh),
                            auth: arrayBufferToBase64(keys.auth)
                        }
                        : null
                }
            ],
            is_subscribed: subscriptionStatus,
            user_id: userId
        };
        const response = await api.post('/Notifications/subscribe', subscriptionData, {
            baseURL: platformHost
        });
        return response.data;
    },
    async submitSurveyResponse(ratingValue, user, platformHost = '') {
        const requestBody = {
            resourceType: 'SurveyResponse',
            status: 'completed',
            authored: new Date().toISOString(),
            isResponseEditable: false,
            subject: {
                userId: user?.id,
                userName: user?.name,
                userEmail: user?.email
            },
            informationSource: {
                userId: user?.id,
                userName: user?.name,
                userEmail: user?.email
            },
            item: [
                {
                    linkId: 'NPS',
                    text: NOTIFICATION_TITLES.FEEDBACK,
                    answer: [
                        {
                            valueInteger: ratingValue
                        }
                    ]
                }
            ],
            meta: {
                deviceId: '',
                clientRequestId: '',
                source: ''
            }
        };
        const response = await api.post('/SurveyResponse/', requestBody, {
            baseURL: platformHost
        });
        return response.data;
    },
    /**
     * Get the user's last survey response to check when they last filled a survey
     * @param userId - The user ID to check
     * @returns Promise with the last survey response data or null if none exists
     */
    async getLastSurveyResponse(userId, platformHost = '') {
        try {
            const response = await api.get('/SurveyResponse/', {
                baseURL: platformHost,
                params: {
                    informationSource: userId,
                    limit: 1,
                    _sort: '-lastUpdated' // Sort by lastUpdated date descending to get the latest
                }
            });
            const entries = response.data?.entry || [];
            if (entries.length > 0 && entries[0]?.resource?.meta?.lastUpdated) {
                return { lastUpdated: entries[0].resource.meta.lastUpdated };
            }
            return { lastUpdated: null };
        }
        catch (error) {
            console.error('Error fetching last survey response:', error);
            return { lastUpdated: null };
        }
    },
    async sendFeedbackEmailNotification(feedbackDetails, platformHost) {
        const typeLabel = feedbackDetails.type === 'issue' ? 'Issue' : 'Enhancement';
        const message = buildFeedbackEmailHtml({
            ...feedbackDetails,
            typeLabel,
            referenceNumber: feedbackDetails.referenceNumber
        });
        const refSuffix = feedbackDetails.referenceNumber
            ? ` - ${feedbackDetails.referenceNumber}`
            : '';
        const notificationTitle = feedbackDetails.type === 'issue'
            ? `We're investigating your reported issue${refSuffix}`
            : `Your request is in motion${refSuffix}`;
        const ownerNotificationTitle = feedbackDetails.type === 'issue'
            ? `[URGENT] New issue submitted for owner follow-up${refSuffix}`
            : `New enhancement submitted for owner follow-up${refSuffix}`;
        const requestBody = {
            resourceType: 'Notification',
            id: '',
            to: [
                {
                    type: 'email',
                    value: feedbackDetails.submittedBy.email
                }
            ],
            ccTo: [],
            bccTo: [],
            from: { type: 'application', value: 'Converge Platform' },
            title: notificationTitle,
            subtitle: '',
            scheduled: '',
            expires: '',
            additionalInformation: [],
            url: '',
            messageType: 'HTML',
            message,
            channels: ['email'],
            viewedDateTime: '',
            attachment: null,
            meta: {
                versionId: 0,
                created: '',
                lastUpdated: '',
                createdBy: '',
                lastUpdatedBy: '',
                isDeleted: false,
                deviceId: '',
                clientRequestId: '',
                requestLogRef: '',
                source: 'feedback-modal'
            }
        };
        const ownerEmails = Array.from(new Set((feedbackDetails.ownerEmails || [])
            .map((email) => email.trim())
            .filter((email) => email.length > 0)));
        if (ownerEmails.length > 0) {
            const ownerMessage = buildFeedbackOwnerEmailHtml({
                ...feedbackDetails,
                typeLabel,
                referenceNumber: feedbackDetails.referenceNumber
            });
            const ownerRequestBody = {
                ...requestBody,
                to: ownerEmails.map((email) => ({
                    type: 'email',
                    value: email
                })),
                title: ownerNotificationTitle,
                message: ownerMessage
            };
            const [response] = await Promise.all([
                api.post('/Notifications/', requestBody, {
                    baseURL: platformHost
                }),
                api.post('/Notifications/', ownerRequestBody, {
                    baseURL: platformHost
                })
            ]);
            return response.data;
        }
        const response = await api.post('/Notifications/', requestBody, {
            baseURL: platformHost
        });
        return response.data;
    }
};
